import { createContext, useContext, useMemo, useCallback, ReactNode } from 'react'
import { AppNotification } from '@digital-twins/shared-types'
import { useAuth } from '@/lib/config/auth-provider'
import { useNotificationsData } from '@/lib/hooks/use-notifications-data'
import { useNotificationsSocket } from '@/lib/hooks/use-notifications-socket'

interface NotificationsContextType {
  notifications: Array<AppNotification>
  unreadCount: number
  lastReadAt: number
  isLoading: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => void
  markAllRead: () => void
}

const EMPTY_NOTIFICATIONS: NotificationsContextType = {
  notifications: [],
  unreadCount: 0,
  lastReadAt: 0,
  isLoading: false,
  error: null,
  hasMore: false,
  loadMore: () => {},
  markAllRead: () => {},
}

const NotificationsContext = createContext<NotificationsContextType>(EMPTY_NOTIFICATIONS)

function NotificationsProviderInner({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { state, dispatch, isLoading, error, loadMore, markAllRead } = useNotificationsData(
    user?.isPlatformAdmin,
    user?.email,
  )

  const handleNewNotification = useCallback(
    (notification: AppNotification) => {
      dispatch({ type: 'PREPEND_ITEM', item: notification })
    },
    [dispatch],
  )

  useNotificationsSocket(handleNewNotification)

  const unreadCount = useMemo(
    () => state.items.filter((n) => n.createdAt > state.lastReadAt).length,
    [state.items, state.lastReadAt],
  )

  const value = useMemo<NotificationsContextType>(
    () => ({
      notifications: state.items,
      unreadCount,
      lastReadAt: state.lastReadAt,
      isLoading,
      error,
      hasMore: state.hasMore,
      loadMore,
      markAllRead,
    }),
    [state.items, state.hasMore, state.lastReadAt, unreadCount, isLoading, error, loadMore, markAllRead],
  )

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()

  if (!isAuthenticated) {
    return (
      <NotificationsContext.Provider value={EMPTY_NOTIFICATIONS}>
        {children}
      </NotificationsContext.Provider>
    )
  }

  return <NotificationsProviderInner>{children}</NotificationsProviderInner>
}

export function useNotifications() {
  return useContext(NotificationsContext)
}
